import { useEffect, useMemo } from "react";
import PageBackButton from "../common/PageBackButton";

const Section = ({ id, eyebrow, title, children }) => {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="space-y-2">
        {eyebrow ? (
          <p className="text-xs font-semibold tracking-wide text-violet-700/80">
            {eyebrow}
          </p>
        ) : null}
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">
          {title}
        </h2>
      </div>
      <div className="mt-3 space-y-3 text-sm sm:text-[15px] leading-6 text-gray-700">
        {children}
      </div>
    </section>
  );
};

const TocLink = ({ href, children }) => (
  <a
    href={href}
    className="block rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-violet-50 hover:text-violet-800 transition"
  >
    {children}
  </a>
);

// components/TermsModal.jsx (route: /term-condition)
export const TermsModal = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "Terms & Conditions • PhonebookPlus";
    return () => {
      document.title = prev;
    };
  }, []);

  const toc = useMemo(
    () => [
      { href: "#top", label: "Overview" },
      { href: "#privacy", label: "Privacy Policy (DPDP Act, 2023)" },
      { href: "#privacy-1", label: "1. Introduction and Legal Context" },
      { href: "#privacy-2", label: "2. Data Collected & Processing" },
      { href: "#privacy-3", label: "3. Information Control Architecture" },
      { href: "#privacy-4", label: "4. Data Localization" },
      { href: "#privacy-5", label: "5. Third‑Party Integrations" },
      { href: "#privacy-6", label: "6. DPDP Rights Execution" },
      { href: "#terms", label: "Terms & Conditions" },
      { href: "#terms-1", label: "1. User Accounts and Eligibility" },
      { href: "#terms-2", label: "2. Data Ownership & IP" },
      { href: "#terms-3", label: "3. Contact Sync Warranty" },
      { href: "#terms-4", label: "4. Billing and Refunds" },
      { href: "#terms-5", label: "5. Prohibited Use" },
      { href: "#terms-6", label: "6. Liability Limitations" },
      { href: "#terms-7", label: "7. Governing Law" },
    ],
    []
  );

  return (
    <div
      id="top"
      className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-white"
    >
      <header className="sticky top-0 z-20 border-b border-violet-100/70 bg-white/80 backdrop-blur">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
          <div className="py-4">
            <PageBackButton label="Back" fallback="/login" />
            <p className="text-xs font-semibold tracking-wide text-violet-700/80">
              PHONEBOOKPLUS TECHNOLOGY &amp; CONSULTING (OPC) PVT. LTD.
            </p>
            <h1 className="mt-1 text-lg sm:text-xl font-semibold text-gray-900">
              Terms &amp; Conditions and Privacy Policy
            </h1>
            <p className="mt-1 text-xs text-gray-500">
              Legal compliance blueprint • DPDP Act, 2023 • India
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-8 sm:py-10 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-6 lg:gap-10">
          {/* TOC */}
          <aside className="lg:sticky lg:top-24">
            <div className="lg:max-h-[calc(100vh-7rem)] lg:overflow-auto lg:pr-1 lg:[scrollbar-width:none] lg:[-ms-overflow-style:none] lg:[&::-webkit-scrollbar]:hidden">
              <div className="rounded-2xl border border-violet-100 bg-white shadow-sm">
                <div className="px-4 py-3 border-b border-violet-100">
                  <p className="text-sm font-semibold text-gray-900">
                    On this page
                  </p>
                </div>
                <nav className="p-2">
                  {toc.map((item) => (
                    <TocLink key={item.href} href={item.href}>
                      {item.label}
                    </TocLink>
                  ))}
                </nav>
              </div>

              <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4 text-xs text-gray-600">
                <p className="font-semibold text-gray-900">Quick notes</p>
                <ul className="mt-2 space-y-2 list-disc pl-4">
                  <li>
                    This page is provided for transparency and compliance
                    reference.
                  </li>
                  <li>
                    For support or compliance requests, contact the Grievance
                    Officer via the in-app route.
                  </li>
                </ul>
              </div>
            </div>
          </aside>

          {/* CONTENT */}
          <div className="min-w-0">
            <div className="rounded-2xl border border-violet-100 bg-white shadow-sm">
              <div className="px-5 sm:px-8 py-6 sm:py-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="space-y-1">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                      LEGAL COMPLIANCE BLUEPRINT
                    </h2>
                    <p className="text-sm text-gray-600">
                      Terms &amp; Conditions and Privacy Policy Framework
                    </p>
                  </div>
                  <div className="rounded-xl bg-violet-50 px-3 py-2 text-xs text-violet-900">
                    <p className="font-semibold">Last updated</p>
                    <p className="text-violet-800/80">May 2026</p>
                  </div>
                </div>

                <div className="mt-5 text-sm sm:text-[15px] leading-6 text-gray-700">
                  <p dir="ltr">
                    This regulatory framework details the structural provisions
                    required for the Privacy Policy and Terms &amp; Conditions
                    governing the PhonebookPlus mobile application. This
                    document establishes the core operational compliance
                    structures, anchoring all data processes, rights, and
                    responsibilities under the Digital Personal Data Protection
                    (DPDP) Act, 2023, and applicable commercial laws.
                  </p>
                </div>

                <div className="my-7 h-px bg-violet-100" />

                <div className="space-y-10">
                  <section id="privacy" className="scroll-mt-24">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                      PART 1: PRIVACY POLICY FRAMEWORK
                    </h2>
                    <p className="mt-2 text-sm sm:text-[15px] leading-6 text-gray-700">
                      PhonebookPlus Technology and Consulting (OPC) Private
                      Limited ("Company", "we", "us", or "our") operates the
                      PhonebookPlus mobile application ("App") and associated
                      digital synchronization services.
                    </p>
                  </section>

                  <Section
                    id="privacy-1"
                    eyebrow="Privacy Policy"
                    title="1. Introduction and Legal Context"
                  >
                    <p dir="ltr">
                      This Privacy Policy serves as our formal statutory
                      disclosure detailing how we collect, process, safeguard,
                      store, and transmit personal data as a Data Fiduciary
                      under Section 2(i) of the Digital Personal Data Protection
                      (DPDP) Act, 2023.
                    </p>
                  </Section>

                  <Section
                    id="privacy-2"
                    eyebrow="Privacy Policy"
                    title="2. Categorization of Data Collected & Processing Methods"
                  >
                    <p dir="ltr">
                      Our data collection model executes strict data
                      minimization principles. We do not use external third‑party
                      scraping, harvesting, or public data enrichment tools
                      independently to build contact profiles. Data processing
                      is classified as follows:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <span className="font-semibold text-gray-900">
                          Primary User Profile Data
                        </span>
                        : During account registration, we collect explicit
                        identifiers including the user's full name, active
                        primary mobile telephone number, verified email address,
                        and authorization credentials. Payment data for premium
                        tiers is securely managed and tokenized through
                        integrated marketplace billing channels.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          Native Device Address Book Synchronization
                        </span>
                        : Upon explicit, affirmative system‑level runtime
                        permission, the App accesses the native operating system
                        address book and processes names and phone numbers to
                        match existing platform members.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          Manual Individual Data Entry
                        </span>
                        : Notes and custom fields entered by the user are stored
                        on our servers.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          Device and Technical Telemetry
                        </span>
                        : Technical attributes (IP address, device identifiers,
                        OS versions, crash logs) required to preserve system
                        stability.
                      </li>
                    </ul>
                  </Section>

                  <Section
                    id="privacy-3"
                    eyebrow="Privacy Policy"
                    title="3. The Granular Directory & Information Control Architecture"
                  >
                    <p dir="ltr">
                      PhonebookPlus operates a user‑controlled, dynamic
                      visibility paradigm. Processing rules include:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <span className="font-semibold text-gray-900">
                          User‑Configured Profile Visibility
                        </span>
                        : Users control which profile elements are exposed to
                        other users based on relationship status.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          The "Added Contacts" Tier
                        </span>
                        : Bilateral connections can view authorized extended
                        profile fields according to each user's privacy
                        parameters.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          The "Unadded / Stored Numbers" Tier
                        </span>
                        : Unadded identifiers are isolated—processing is limited
                        to basic indexing to facilitate core matching.
                      </li>
                    </ul>
                  </Section>

                  <Section
                    id="privacy-4"
                    eyebrow="Privacy Policy"
                    title="4. Infrastructure Architecture and Absolute Data Localization"
                  >
                    <p dir="ltr">
                      In compliance with data localization requirements of the
                      DPDP Act, 2023, primary user data and related indices are
                      hosted on secure cloud infrastructure physically situated
                      within India, unless separately authorized under statutory
                      regulation.
                    </p>
                  </Section>

                  <Section
                    id="privacy-5"
                    eyebrow="Privacy Policy"
                    title="5. Third‑Party Integrations, Sub‑Processors, and Google API Compliance"
                  >
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <span className="font-semibold text-gray-900">
                          Dynamic Sub‑Processor Authorization
                        </span>
                        : Specialized vendors may be used for hosting, telemetry,
                        notifications, analytics, and support. A list is
                        available upon request.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          Google API Services "Limited Use" Compliance
                        </span>
                        : Data retrieved via Google APIs is confined to
                        user‑facing platform enhancements and is not used for
                        advertising monetization or human review.
                      </li>
                    </ul>
                  </Section>

                  <Section
                    id="privacy-6"
                    eyebrow="Privacy Policy"
                    title="6. Digital Personal Data Protection (DPDP) Act Rights Execution"
                  >
                    <p dir="ltr">
                      In accordance with Chapter III of the DPDP Act, 2023, Data
                      Principals are afforded statutory rights which can be
                      enforced through the App:
                    </p>

                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <table className="w-full table-auto border-collapse text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-3 text-left font-semibold text-gray-900 border-b border-gray-200">
                              Statutory Right (DPDP Act, 2023)
                            </th>
                            <th className="p-3 text-left font-semibold text-gray-900 border-b border-gray-200">
                              In‑App Enforcement Mechanism
                            </th>
                          </tr>
                        </thead>
                        <tbody className="[&>tr:nth-child(even)]:bg-gray-50/50">
                          <tr>
                            <td className="p-3 align-top border-b border-gray-200">
                              Right to Information &amp; Summary (Section 11)
                            </td>
                            <td className="p-3 align-top border-b border-gray-200">
                              Request a summary of personal data processed and a
                              description of processing activities via the
                              Privacy dashboard.
                            </td>
                          </tr>
                          <tr>
                            <td className="p-3 align-top border-b border-gray-200">
                              Right to Correction &amp; Erasure (Section 12)
                            </td>
                            <td className="p-3 align-top border-b border-gray-200">
                              Edit personal data and initiate account erasure to
                              purge profiles and decouple contact syncs.
                            </td>
                          </tr>
                          <tr>
                            <td className="p-3 align-top border-b border-gray-200">
                              Right to Grievance Redressal (Section 13)
                            </td>
                            <td className="p-3 align-top border-b border-gray-200">
                              Contact the designated Grievance Officer to log
                              and resolve compliance issues within statutory
                              timelines.
                            </td>
                          </tr>
                          <tr>
                            <td className="p-3 align-top">
                              Right to Nominate (Section 14)
                            </td>
                            <td className="p-3 align-top">
                              Nominate another individual to execute data rights
                              in the event of death or incapacity.
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Section>

                  <section id="terms" className="scroll-mt-24">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                      PART 2: TERMS &amp; CONDITIONS
                    </h2>
                  </section>

                  <Section
                    id="terms-1"
                    eyebrow="Terms & Conditions"
                    title="1. User Accounts and Eligibility"
                  >
                    <p dir="ltr">
                      By establishing an account, the user certifies legal
                      capacity and an age of at least 18 years. The user is
                      responsible for safeguarding access tokens or passwords.
                    </p>
                  </Section>

                  <Section
                    id="terms-2"
                    eyebrow="Terms & Conditions"
                    title="2. Data Ownership, Intellectual Property, and Limited Processing Licenses"
                  >
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <span className="font-semibold text-gray-900">
                          User Ownership
                        </span>
                        : The user retains rights over text fields and phone
                        numbers they upload or sync.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          Limited Operational License Grant
                        </span>
                        : A revocable license to host and process uploaded data
                        solely to execute contact management operations, ending
                        upon account termination.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          Platform Intellectual Property
                        </span>
                        : Software, algorithms, design, trademarks, and database
                        structures remain the exclusive property of the Company.
                      </li>
                    </ul>
                  </Section>

                  <Section
                    id="terms-3"
                    eyebrow="Terms & Conditions"
                    title="3. Contact Synchronization Representations and Legal Liability Shield"
                  >
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                      <p className="font-semibold">
                        Critical user warranty requirement
                      </p>
                      <p className="mt-2">
                        By enabling address book synchronization, the user
                        represents that they have obtained necessary permissions
                        or consents to upload and process third‑party phone
                        numbers. The user agrees to indemnify the Company from
                        claims arising from unauthorized uploads.
                      </p>
                    </div>
                  </Section>

                  <Section
                    id="terms-4"
                    eyebrow="Terms & Conditions"
                    title="4. Freemium Monetization Mechanics, Financial Terms, and In‑App Billing"
                  >
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <span className="font-semibold text-gray-900">
                          Tiered Accessibility
                        </span>
                        : Basic features are free; premium features require a
                        paid subscription.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          Third‑Party Billing Isolation
                        </span>
                        : Payments are processed by marketplace billing or
                        compliant gateways; the Company does not store raw
                        payment credentials.
                      </li>
                      <li>
                        <span className="font-semibold text-gray-900">
                          Refund Limitations
                        </span>
                        : Premium purchases are non‑refundable. Cancellation
                        stops future billing cycles.
                      </li>
                    </ul>
                  </Section>

                  <Section
                    id="terms-5"
                    eyebrow="Terms & Conditions"
                    title="5. Prohibited Use, Security Enforcement, and Behavioral Restrictions"
                  >
                    <p dir="ltr">
                      Users are prohibited from abusive behavior including:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        Distribution or storage of unsolicited bulk communications
                        (spam lists) or illegally acquired numbers.
                      </li>
                      <li>
                        Automated extraction technologies (scrapers, data miners,
                        spiders) to map database endpoints.
                      </li>
                      <li>
                        Reverse‑engineering, decompiling, or attempting to extract
                        source code.
                      </li>
                      <li>
                        Violating applicable intermediary guidelines and digital
                        media ethics rules.
                      </li>
                    </ul>
                  </Section>

                  <Section
                    id="terms-6"
                    eyebrow="Terms & Conditions"
                    title="6. Structural Limitations of Liability and System Warranties"
                  >
                    <p dir="ltr">
                      The application is provided on an “AS‑IS” and “AS‑AVAILABLE”
                      basis. Liability is capped to the amount paid by the user
                      during the preceding twelve (12) months, to the maximum
                      extent permitted under applicable law.
                    </p>
                  </Section>

                  <Section
                    id="terms-7"
                    eyebrow="Terms & Conditions"
                    title="7. Governing Law and Exclusive Dispute Jurisdiction"
                  >
                    <p dir="ltr">
                      These Terms are governed by the laws of India. Disputes
                      must be filed exclusively in competent courts in Mumbai,
                      Maharashtra, India.
                    </p>
                  </Section>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};